import { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchStateEmployeeFields, getStateEmployeeSections } from "store/stateEmployeeSlice";
import { getRoles } from "store/userSlice";
import { useTranslation } from "translation/useTranslation";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { empty, formatNumberTo, isTrue, removeTrailingZeros } from "helpers/common";
import sanitize from "helpers/sanitize";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { fetchRegularEvaluationLevels, getRegularEvaluationLevels } from "store/balanceScorecardSlice";
import { getGeneralSettings } from "store/generalSettingsSlice";
import { getAllCustomNames, getAllFields } from "store/fieldsSlice";
import ExpandableHtml from "components/ui/ExpandableHtml";

dayjs.extend(customParseFormat);

function Section({ section, employee, regularEvaluationLevels, isSecondaryEvaluator, firstEvaluatorName, secondaryEvaluatorName, stateEvaluationFlow, customConfig }) {
  const { translate, editableTranslate } = useTranslation();

  const generalSettings = useSelector(getGeneralSettings);

  const resultAsNumeric = useMemo(() => {
    if (!generalSettings) return false;

    // generalSettings is an array of object, search the key 'balanced_scorecard_result_format'
    const setting = generalSettings.find(s => s.key === 'balanced_scorecard_result_format');
    if (setting && setting.value === 'numeric') {
      return true;
    }

    return false;
  }, [generalSettings]);

  const workingHoursAsPtFt = useMemo(() => {
    if (!generalSettings) return true;

    const setting = generalSettings.find(s => s.key === 'working_hours_display');
    if (setting && setting.value === 'parttime_fulltime') return true;
    return false;
  }, [generalSettings]);

  const getLabeledValue = (value) => {
    if (empty(value)) return '-';

    let tempValue = value;
    if (typeof tempValue === 'string') tempValue = parseFloat(formatNumberTo(tempValue, '.'));

    if (resultAsNumeric) return formatNumberTo(tempValue);

    for (let i = regularEvaluationLevels.length - 1; i >= 0; i--) {
      const lvl = regularEvaluationLevels[i].level;
      const val = parseFloat(formatNumberTo(regularEvaluationLevels[i].value, '.'));
      if (val % 1 === 0.5) {
        if (tempValue === val) return lvl;
      } else if (Math.abs(val - tempValue) < 0.5) return lvl;
    }

    return '-';
  };

  const getEmployeeInformationMeta = (identifier) => {
    return employee?.employeeInformation?.data?.find((item) => item?.key === identifier) || null;
  };

  const getVal = (inputField) => {
    let val = "-";

    val = employee?.information?.[inputField.identifier] || "-";

    if (inputField?.stored_in_table === 'employees') {
      val = (employee?.[inputField.identifier] || '-');
    }

    if ((inputField?.field_type === 'integer' || inputField?.field_type === "float" || inputField?.field_type === 'numeric' || inputField?.field_type.includes("decimal")) && val !== '-') {
      val = removeTrailingZeros(val);
    }

    if (inputField.field_type === "boolean") {
      val = isTrue(val) ? translate("form.yes", "Ja") : "-";
    }

    if (inputField.field_type === "radio" && val !== '-') {
      const opt = inputField?.options?.find(o => o?.value === val);
      if (opt) {
        val = translate(opt?.label_translation_key, opt?.value);
        if (opt?.with_input_text) {
          const inputIdentifier = `${inputField.identifier}_${opt?.value}_input_text`;
          let inputTextVal = employee?.information?.[inputIdentifier] || "-";
          if (inputField?.stored_in_table === 'employees') {
            inputTextVal = (employee?.[inputIdentifier] || '-');
          }

          val += ': ' + inputTextVal;
        }
      }
    }

    if (inputField?.identifier === 'working_hours' && workingHoursAsPtFt && val !== '-') {
      if (parseFloat(formatNumberTo(val, '.')) >= 100) {
        val = translate('general_settings.balanced_scorecard.working_hours_display.fulltime');
      } else {
        val = translate('general_settings.balanced_scorecard.working_hours_display.parttime');
      }
    } else if (inputField?.suffix && val !== "-") {
      val = val + inputField?.suffix;
    }

    if (inputField.identifier === 'previous_evaluation_results') {
      val = employee?.previous_evaluation_results?.regular_evaluation?.result ? getLabeledValue(employee?.previous_evaluation_results?.regular_evaluation?.result) : '-';
    } else if (!empty(val) && inputField.identifier === 'pay_grade') {
      val = employee?.pay_grade_label || val;
    } else if (!empty(val) && inputField.identifier === 'job_differs_from_classification') {
      const resolvedLabel = getEmployeeInformationMeta(inputField.identifier)?.resolved_label;
      if (resolvedLabel) val = resolvedLabel;
    }

    return val;
  };

  const getAllFields = (section) => {
    const arr = [];
    for (const field of section.fields) {
      if (field?.field_type === 'compound') {
        if (field?.components?.[0]?.type === 'layout') {
          for (const field2 of field?.components[0].columns) {
            const vals = [];
            let hasVal = false;
            for (const comp of field2?.components) {
              if (comp?.type === 'input') {
                const val = getVal({
                  ...comp,
                  field_type: comp?.input_type
                });
                if (val && val !== '-') {
                  vals.push(val);
                  hasVal = true;
                }
              } else if (comp?.type === 'text') {
                vals.push(comp?.text || comp?.fallback_text);
              }
            }
            const value = vals.join(' ');
            if (hasVal) {
              arr.push({
                ...field2,
                name: field2?.label?.text,
                translation_key: field2?.label?.translation_key,
                value
              });
            }
          }
        } else {
          for (const comp of field.components) {
            if (comp?.type === 'input') {
              const val = getVal({
                ...comp,
                field_type: comp?.input_type
              });
              if (val && val !== '-') {
                const tempObj = {};

                if (field?.checkbox_label) {
                  if (field?.translation_key) {
                    comp.translation_key = field.translation_key;
                  }

                  let checkboxChecked = employee?.information?.[field?.identifier + '_checkbox'];
                  if (isTrue(checkboxChecked)) {
                    tempObj.checkbox_label = field?.checkbox_label;
                    tempObj.checkbox_checked = true;
                  }
                }

                if (field?.notes_prefix_text_translation_key) {
                  tempObj.notes_prefix_text_translation_key = field?.notes_prefix_text_translation_key;
                }

                arr.push({
                  ...comp,
                  value: val,
                  ...tempObj
                });
              }
            }
          }
        }
      } else {
        const val = getVal(field);
        if (val && val !== '-') {
          const tempObj = {};

          if (field?.checkbox_label) {
            let checkboxChecked = employee?.information?.[field?.identifier + '_checkbox'];
            if (isTrue(checkboxChecked)) {
              tempObj.checkbox_label = field?.checkbox_label;
              tempObj.checkbox_checked = true;
            }
          }

          arr.push({
            ...field,
            value: val,
            ...tempObj
          });
        }
      }
    }
    return arr;
  };

  const flatenedFields = useMemo(() => {
    const fields = getAllFields(section);
    if (section?.identifier === 'assessment_criteria') {
      const firstEvObj = {
        name: 'Erstbeurteiler*in',
        translation_key: 'state_employee.first_evaluator',
        value: firstEvaluatorName
      };

      const secondaryEvObj = {
        name: 'Zweitbeurteiler*in',
        translation_key: 'state_employee.second_evaluator',
        value: secondaryEvaluatorName
      };

      if (customConfig) {
        if (!isSecondaryEvaluator && customConfig?.secondary_evaluation_enabled !== false && stateEvaluationFlow?.flow !== 'primary_evaluator_only') {
          fields.unshift(secondaryEvObj);
        } else if (isSecondaryEvaluator && customConfig?.initial_evaluation_enabled !== false) {
          fields.unshift(firstEvObj);
        }
      }
    }
    return fields;
  }, [section, employee, isSecondaryEvaluator, firstEvaluatorName, secondaryEvaluatorName, stateEvaluationFlow, customConfig]);

  const additionalSectionFields = useMemo(() => {
    if (!section?.additional_section) return null;
    return getAllFields(section.additional_section);
  }, [section, employee]);

  return (flatenedFields.length > 0 || (section?.additional_section && additionalSectionFields.length > 0)) ? <div className="col-6 mb-4">
    <div className="col-compact">
      {
        flatenedFields.length > 0 && <>
          <h5 className="mb-2 col-compact-header break-word">
            {editableTranslate(section.translation_key, section.name)}{" "}
            {section?.info_tooltip_translation_key && (
              <i
                className="bi bi-info-circle"
                data-tooltip-id="state-employee-section-tooltip"
                data-tooltip-content={translate(section?.info_tooltip_translation_key)}
              ></i>
            )}
          </h5>

          <div className="gray-box-2 px-3 py-2">
            {
              flatenedFields.map((field, i) => <div key={i} {...(field?.identifier ? { 'data-testid': `field-${field.identifier}` } : {})} className={`row ${i < flatenedFields.length - 1 ? 'mb-3' : ''}`}>
                {
                  flatenedFields.length === 1 && (flatenedFields[0]?.field_type === 'text' || flatenedFields[0]?.input_type === 'text') ?
                    <div className="col-12">
                      {field?.notes_prefix_text_translation_key && (
                        <div className="mb-2">
                          <span>
                            <strong>{editableTranslate("state_employee.notes.attention")}</strong>{" "}
                            {editableTranslate(field?.notes_prefix_text_translation_key)}
                          </span>
                        </div>
                      )}
                      {
                        field?.checkbox_label && <label className={`font-weight-bold mb-2`}>
                          <input
                            type="checkbox"
                            className="mr-2 d-inline-flex v-middle"
                            checked={field?.checkbox_checked}
                            disabled={true}
                            data-testid={`field-checkbox-${field.translation_key}`}
                          />
                          <span className={"v-middle break-word"}>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</span>
                        </label>
                      }

                      <ExpandableHtml html={field?.value || "-"} className="fade-gray-bg-2" />
                    </div> :
                    <Fragment>
                      <div className="col-6">
                        {(i === 0 && field?.notes_prefix_text_translation_key) && (
                          <div className="mb-2">
                            <span>
                              <strong>{editableTranslate("state_employee.notes.attention")}</strong>{" "}
                              {editableTranslate(field?.notes_prefix_text_translation_key)}
                            </span>
                          </div>
                        )}
                        {
                          (i === 0 && field?.checkbox_label) && <label className={`font-weight-bold mb-2`}>
                            <input
                              type="checkbox"
                              className="mr-2 d-inline-flex v-middle"
                              checked={field?.checkbox_checked}
                              disabled={true}
                              data-testid={`field-checkbox-${field.translation_key}`}
                            />
                            <span className={"v-middle break-word"}>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</span>
                          </label>
                        }

                        {
                          !field?.checkbox_label && <span className="font-weight-bold break-word">{field?.custom_name || editableTranslate(field.translation_key, field.name)}
                            {
                              (field?.identifier === 'job_differs_from_classification' && field?.custom_name) && <i className="bi bi-info-circle ml-1" data-tooltip-id="state-employee-compact-field-tooltip" data-tooltip-content={translate(field.translation_key, field.name)}></i>
                            }
                          </span>
                        }
                      </div>
                      <div className="col-6">
                        <div className="break-word" dangerouslySetInnerHTML={{ __html: sanitize(field?.value || "-") }} />
                      </div>
                    </Fragment>
                }
              </div>)
            }
          </div>
        </>
      }

      {
        (section?.additional_section && additionalSectionFields.length > 0) && <>
          <h5 className="mt-3">
            {editableTranslate(section.additional_section.translation_key, section.additional_section.name)}{" "}
            {section?.additional_section?.info_tooltip_translation_key && (
              <i
                className="bi bi-info-circle"
                data-tooltip-id="state-employee-section-tooltip"
                data-tooltip-content={translate(section?.additional_section?.info_tooltip_translation_key)}
              ></i>
            )}
          </h5>

          <div className="gray-box-2 px-3 py-2">
            {
              additionalSectionFields.map((field, i) => <div key={i} {...(field?.identifier ? { 'data-testid': `field-${field.identifier}` } : {})} className={`row ${i < additionalSectionFields.length - 1 ? 'mb-3' : ''}`}>
                {
                  additionalSectionFields.length === 1 && (additionalSectionFields[0]?.field_type === 'text' || additionalSectionFields[0]?.input_type === 'text') ?
                    <div className="col-12">
                      {field?.notes_prefix_text_translation_key && (
                        <div className="mb-2">
                          <span>
                            <strong>{editableTranslate("state_employee.notes.attention")}</strong>{" "}
                            {editableTranslate(field?.notes_prefix_text_translation_key)}
                          </span>
                        </div>
                      )}
                      {
                        field?.checkbox_label && <label className={`font-weight-bold mb-2`}>
                          <input
                            type="checkbox"
                            className="mr-2 d-inline-flex v-middle"
                            checked={field?.checkbox_checked}
                            disabled={true}
                            data-testid={`field-checkbox-${field.translation_key}`}
                          />
                          <span className={"v-middle break-word"}>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</span>
                        </label>
                      }

                      <ExpandableHtml html={field?.value || "-"} className="fade-gray-bg-2" />
                    </div> :
                    <Fragment>
                      <div className="col-6">
                        {(i === 0 && field?.notes_prefix_text_translation_key) && (
                          <div className="mb-2">
                            <span>
                              <strong>{editableTranslate("state_employee.notes.attention")}</strong>{" "}
                              {editableTranslate(field?.notes_prefix_text_translation_key)}
                            </span>
                          </div>
                        )}
                        {
                          (i === 0 && field?.checkbox_label) && <label className={`font-weight-bold mb-2`}>
                            <input
                              type="checkbox"
                              className="mr-2 d-inline-flex v-middle"
                              checked={field?.checkbox_checked}
                              disabled={true}
                              data-testid={`field-checkbox-${field.translation_key}`}
                            />
                            <span className={"v-middle break-word"}>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</span>
                          </label>
                        }

                        {
                          !field?.checkbox_label && <span className="font-weight-bold break-word">{field?.custom_name || editableTranslate(field.translation_key, field.name)}</span>
                        }
                      </div>
                      <div className="col-6">
                        <div className="break-word" dangerouslySetInnerHTML={{ __html: sanitize(field?.value || "-") }} />
                      </div>
                    </Fragment>
                }
              </div>)
            }
          </div>
        </>
      }
    </div>

    <ReactTooltip id="state-employee-compact-field-tooltip" place="bottom" />
  </div> : <></>;
}

export default function StateEmployeeDataCompactReadOnly({ employee, settingIdentifier = null, isSecondaryEvaluator, firstEvaluatorName, secondaryEvaluatorName, stateEvaluationFlow, customConfig, setCompactDataLoaded = null }) {
  const dispatch = useDispatch();

  const allFields = useSelector(getAllFields);
  const fieldCustomNames = useSelector(getAllCustomNames);

  const regularEvaluationLevels = useSelector(getRegularEvaluationLevels);

  const sections = useSelector(getStateEmployeeSections);

  useEffect(() => {
    dispatch(fetchStateEmployeeFields());

    if (regularEvaluationLevels?.length < 1) dispatch(fetchRegularEvaluationLevels());
  }, [dispatch]);

  const filteredSections = useMemo(() => {
    if (!settingIdentifier) return sections;

    if (sections) {
      const tempArr = [];
      for (const section of sections) {
        const fields = section?.fields?.filter(f => {
          if (f.hasOwnProperty('applicable_settings')) {
            if (f.applicable_settings.includes(settingIdentifier)) return true;
            return false;
          }
          return true;
        });

        if (fields.length > 0) {
          tempArr.push({
            ...section,
            fields
          });
        }
      }

      const newArr = structuredClone(tempArr);

      if (!allFields) return newArr;

      const enabledFields = allFields.filter(f => f?.enabled).map(f => f.translation_key);

      for (const section of newArr) {
        section.fields = section?.fields?.filter(field => {
          if (field?.field_type === 'compound') {
            field.components = field?.components?.map(component => {
              if (component?.type === 'layout') {
                component.columns = component?.columns?.map(col => {
                  col.components = col?.components?.filter(component2 => {
                    if (component2?.type === 'input' && fieldCustomNames && fieldCustomNames.hasOwnProperty(component2?.translation_key)) {
                      component2.custom_name = fieldCustomNames[component2?.translation_key];
                    }

                    return (
                      component2?.type !== 'input' ||
                      !component2?.is_importable ||
                      enabledFields.includes(component2?.translation_key)
                    );
                  });
                  if (col.components.length === 0) return null;
                  return col;
                }).filter(Boolean);
                if (component.columns.length === 0) return null;
                return component;
              } else if (fieldCustomNames && fieldCustomNames.hasOwnProperty(component?.translation_key)) {
                component.custom_name = fieldCustomNames[component?.translation_key];
              }

              return (!component?.is_importable || enabledFields.includes(component?.translation_key)) ? component : null;
            }).filter(Boolean);

            return true;
          } else if (fieldCustomNames && fieldCustomNames.hasOwnProperty(field?.translation_key)) {
            field.custom_name = fieldCustomNames[field?.translation_key];
          }

          return !field?.is_importable || enabledFields.includes(field?.translation_key);
        });
      }

      const newArr2 = structuredClone(newArr);

      const filteredArr = [];

      for (const section of newArr2) {
        section.fields = section?.fields?.filter(f => {
          let showConditional = true;

          if (f?.show_conditional) {
            const val = employee?.[f?.show_conditional?.identifier] || employee?.information?.[f?.show_conditional?.identifier];

            if (typeof f?.show_conditional?.equals === "boolean") {
              showConditional = f?.show_conditional?.equals === isTrue(val);
            } else if (f?.show_conditional?.equals === null) {
              showConditional = empty(val);
            } else {
              showConditional = val === f?.show_conditional?.equals;
            }
          }

          return f.is_active && !f?.hide_for_evaluator && showConditional;
        });

        if (section?.is_active && section.fields.length > 0) {
          if (section?.identifier === 'work_related' && filteredArr?.[2]) {
            filteredArr[2].additional_section = section;
          } else {
            filteredArr.push(section);
          }
        }
      }

      return filteredArr;
    }
    return [];
  }, [sections, settingIdentifier, allFields, employee]);

  useEffect(() => {
    if (!filteredSections || !setCompactDataLoaded) return;

    const id = setTimeout(() => {
      setCompactDataLoaded(true);
    }, 100);

    return () => clearTimeout(id);
  }, [filteredSections, setCompactDataLoaded]);

  return <Fragment>
    {
      filteredSections && filteredSections.map((section, i) => <Section
        key={i}
        section={section}
        employee={employee}
        regularEvaluationLevels={regularEvaluationLevels}
        isSecondaryEvaluator={isSecondaryEvaluator}
        firstEvaluatorName={firstEvaluatorName}
        secondaryEvaluatorName={secondaryEvaluatorName}
        stateEvaluationFlow={stateEvaluationFlow}
        customConfig={customConfig} />)
    }

    <ReactTooltip id="state-employee-section-tooltip" place="bottom" />
  </Fragment>;
}
